/* SisVariantSync: late listeners re-sync themselves (owner 2026-10-16,
   docs/TECH-DEBT.md "Late listeners miss load-time events", option B).

   sis-buy-summary and sis-gallery-badges render variant-dependent markup from
   productSelect events, and they are module scripts that load AFTER
   sis-option-tiles.js. A selection made before one of them is defined (a
   load-time default / restore / auto-upsize click, or any click on a slow
   phone past the tiles' 2 s gate) never reaches it, and it kept the server's
   first-variant HTML: on 16 Oct 2026 the summary said '8" X 10" ... $179'
   while the cart had 12" X 16" ($229).

   So a block does not depend on having heard the event. It records the
   variant its markup was rendered for (data-variant-id) and calls
   watch(el, onStale): when it connects, and whenever the section's variant
   picker re-renders, the picker's own selected-variant JSON (the stock
   `variant-picker script[type="application/json"]`, which variant-picker.js
   itself reads) is compared with that id. A mismatch fetches the section
   once for the picker's variant and hands the parsed document to onStale,
   where the block swaps itself exactly as it does on productSelect. When the
   events arrive in time the ids already match and nothing is fetched.

   Classic script, loaded deferred from snippets/custom-globals.liquid on
   product pages, so it runs before the blocks' module scripts. */
(function () {
  if (window.SisVariantSync) return;

  function selectedId(picker) {
    var script = picker.querySelector('script[type="application/json"]');
    try {
      var variant = JSON.parse((script && script.textContent) || 'null');
      return variant && variant.id ? String(variant.id) : '';
    } catch (e) {
      return '';
    }
  }

  // the summary and the badge sit in the same section and go stale together:
  // they share one request per URL while it is in flight
  var inflight = {};

  function fetchSection(section, picker, variantId) {
    var sectionId = (section.id || '').replace(/^shopify-section-/, '');
    if (!sectionId) return Promise.resolve(null);
    var base = (picker.dataset.productUrl || window.location.pathname).split('?')[0];
    var params = new URLSearchParams({ variant: variantId, section_id: sectionId });
    // an alternate template (?view=) renders its own section ids
    var view = new URLSearchParams(window.location.search).get('view');
    if (view) params.set('view', view);
    var url = base + '?' + params.toString();
    if (!inflight[url]) {
      inflight[url] = fetch(url)
        .then(function (response) { return response.ok ? response.text() : ''; })
        .then(function (text) { return text ? new DOMParser().parseFromString(text, 'text/html') : null; })
        .finally(function () { delete inflight[url]; });
    }
    return inflight[url];
  }

  /**
   * @param {HTMLElement} el - the block; reads el.dataset.variantId (and
   *   productId, to pick the right picker when a section has several)
   * @param {(doc: Document) => void} onStale - swap from the fetched section
   * @returns {() => void} stop watching
   */
  function watch(el, onStale) {
    var section = el.closest('.shopify-section, [id^="shopify-section"]');
    if (!section) return function () {};
    var pid = el.dataset.productId;
    var picker =
      (pid && section.querySelector('variant-picker[data-product-id="' + pid + '"]')) ||
      section.querySelector('variant-picker');
    if (!picker) return function () {};
    var timer = 0;
    var pending = '';
    var fetched = {}; // one fetch per variant: a section without the block must not refetch on every morph
    function check() {
      var id = selectedId(picker);
      if (!id || id === el.dataset.variantId || id === pending || fetched[id]) return;
      pending = id;
      fetched[id] = true;
      fetchSection(section, picker, id)
        .then(function (doc) {
          pending = '';
          if (selectedId(picker) !== id) return check(); // the customer moved on meanwhile
          if (doc && id !== el.dataset.variantId) onStale(doc);
        })
        .catch(function () {
          pending = '';
          delete fetched[id]; // a failed request may try again on the next re-render
        });
    }
    // the picker morphs its subtree on every variant change; judge once the
    // churn settles, after the block's own productSelect swap had its chance
    var observer = new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(check, 150);
    });
    observer.observe(picker, { childList: true, subtree: true, characterData: true });
    check();
    return function () {
      observer.disconnect();
      clearTimeout(timer);
    };
  }

  window.SisVariantSync = { watch: watch };
})();
